import { Container, Paper } from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import { MdCategory, MdDelete, MdAdd } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import { getAllCategoriesList } from "../redux/actions/contents";
import { IoArrowBack } from "react-icons/io5";
import { motion } from "framer-motion";
import PageName from "../Components/global/PageName";

export default function CategoryManager() {
  const dispatch = useDispatch();
  const { categoriesList, loading: categoriesLoading } = useSelector(
    (state) => state?.content
  );
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  useEffect(() => {
    dispatch(getAllCategoriesList(1, 100));
  }, [sync, dispatch]);

  const handleAddCategory = async (e) => {
    if (e) e.preventDefault();
    if (!category || category.trim().length === 0) {
      toast.warning("Please enter a category name.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(`${server}/category/new`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          category: category.trim(),
        },
      });
      toast.success(data?.message || "Category added successfully.");
      setCategory("");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (cat) => {
    const userRes = window.confirm(
      `Are you sure you want to delete the category "${cat?.category}"?`
    );
    if (!userRes) return;

    try {
      setLoading(true);
      const { data } = await axios.delete(`${server}/category/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          categoryId: cat?.id,
        },
      });
      toast.success(data?.message || "Category deleted.");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" className="py-4">
      <PageName
        title="Category Manager"
        subTitle="Organize portfolio galleries, services, and pricing packages."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
        {/* Left Column: Add Category Form */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 sticky top-24">
            <h3 className="text-base font-bold text-slate-800 mb-1 flex items-center gap-2">
              <MdCategory className="text-amber-600" /> Create New Category
            </h3>
            <p className="text-xs text-slate-500 mb-6">
              Add a new category classification to organize content across the site.
            </p>

            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Category Name
                </label>
                <input
                  type="text"
                  placeholder="e.g. Bridal Glam, Haldi, Editorial..."
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-slate-300 focus:outline-none focus:border-amber-600 transition-colors"
                />
              </div>

              <div className="pt-2 flex flex-col gap-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs tracking-wider uppercase shadow-md shadow-amber-600/20 transition-all disabled:opacity-50"
                >
                  <MdAdd size={16} />
                  Add Category
                </button>

                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full flex items-center justify-center gap-1 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium text-xs transition-colors"
                >
                  <IoArrowBack size={14} /> Back
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: Category List Cards */}
        <div className="lg:col-span-2">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  All Active Categories
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Total Categories: <span className="font-semibold text-amber-600">{categoriesList?.length || 0}</span>
                </p>
              </div>
            </div>

            {categoriesList?.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-xs">
                No categories created yet. Use the form on the left to add one.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categoriesList?.map((cat, idx) => (
                  <motion.div
                    key={cat.id || idx}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    className="p-4 rounded-xl border border-slate-200 hover:border-amber-400 bg-slate-50/50 hover:bg-amber-50/30 flex items-center justify-between transition-all group shadow-2xs"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
                        {idx + 1}
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-800 capitalize">
                          {cat?.category}
                        </h4>
                        <span className="text-[0.65rem] text-slate-400">ID: #{cat?.id}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDeleteCategory(cat)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Category"
                    >
                      <MdDelete size={18} />
                    </button>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {(loading || categoriesLoading) && <Loading />}
    </Container>
  );
}
